import React from "react";
import moment from "moment/moment";
import cloneDeep from "lodash/cloneDeep";

const useBlockTime = (shop, customDate) => {
  const currentDay = customDate;
  const [__blockTimes, setBlockTimes] = React.useState([]);
  const [currDay, setCurrDayWorkingHours] = React.useState([]);
  const [workingHours, setWorkingHours] = React.useState([]);
  const WORKING_HOURS = shop.workingHoursOfWeek;
  const bookingTimeBlock = shop.booking_time_block;

  React.useEffect(() => {
    handleSetBlockTime();
  }, [customDate]);

  const handleSetBlockTime = () => {
    const currDayWorkingHours = getCurrentDayWorkingHour();
    const blockTime = calculateTimeBlocks(currDayWorkingHours);
    const workingDayOfHours = getWorkingHoursOfDay(currDayWorkingHours);
    setBlockTimes(blockTime);
    setWorkingHours(workingDayOfHours);
  };

  const calculateTimeBlocks = (workingHour) => {
    const blocks = [];
    workingHour.forEach((item) => {
      blocks.push(...getBlockTimes(item.start, item.end));
    });

    return blocks;
  };

  const getBlockTimes = (start, end) => {
    const block = [];
    let startTime = start;
    while (moment(startTime).isBefore(moment(end))) {
      block.push(moment(startTime).format("HH:mm"));
      startTime = moment(startTime).add(bookingTimeBlock, "minutes");
    }
    return block;
  };

  const getCurrentWeekOfDay = React.useMemo(() => {
    return moment(currentDay).locale("en").format("ddd").toUpperCase();
  }, [currentDay]);

  const getPreviousDay = React.useMemo(() => {
    return moment(currentDay)
      .add(-1, "day")
      .locale("en")
      .format("ddd")
      .toUpperCase();
  }, [currentDay]);

  const getCurrentDayWorkingHours = () => {
    const currentWeekOfDay = getCurrentWeekOfDay;
    let currDayWorkingHours = WORKING_HOURS.filter(
      (item) => item.dayOfWeek === currentWeekOfDay
    ).map((item) => item.workingHours);

    if (currDayWorkingHours.length) {
      currDayWorkingHours = currDayWorkingHours.reduce((acc, curr) => {
        acc.push(...curr);
        return acc;
      }, []);

      currDayWorkingHours = currDayWorkingHours.filter(
        (item) => item.start < "2400"
      );

      currDayWorkingHours = currDayWorkingHours.map((item) => {
        const hour = cloneDeep(item);

        if (hour.end >= "2400") {
          hour.end = "2359";
        }

        return hour;
      });
    }

    setCurrDayWorkingHours(currDayWorkingHours);
    return currDayWorkingHours;
  };

  const getPrevDayWorkingHours = () => {
    const previousDay = getPreviousDay;
    let prevDayWorkingHours = WORKING_HOURS.filter(
      (item) => item.dayOfWeek === previousDay
    ).map((item) => item.workingHours);

    if (prevDayWorkingHours.length) {
      prevDayWorkingHours = prevDayWorkingHours.reduce((acc, curr) => {
        acc.push(...curr);
        return acc;
      }, []);

      prevDayWorkingHours = prevDayWorkingHours
        .filter((item) => item.end > "2400")
        .map((item) => {
          const hour = cloneDeep(item);
          if (hour.start <= "2400") {
            hour.start = "0000";
          } else {
            const startHour = (+hour.start.substr(0, 2) - 24).toString();
            hour.start =
              startHour.length === 1
                ? `0${startHour}${hour.start.substr(2)}`
                : `${startHour}${hour.start.substr(2)}`;
          }

          const endHour = (+hour.end.substr(0, 2) - 24).toString();
          hour.end =
            endHour.length === 1
              ? `0${endHour}${hour.end.substr(2)}`
              : `${endHour}${hour.end.substr(2)}`;

          return hour;
        });
    }
    return prevDayWorkingHours;
  };

  const sortCurrentDayWorkingHour = (currentDay, previousDay) => {
    return [...currentDay, ...previousDay].sort((a, b) => {
      if (a.start > b.start) {
        return 1;
      } else {
        return -1;
      }
    });
  };

  const formatTimeForCurrentDay = (currDayWorkingHours) => {
    return currDayWorkingHours.map((item) => {
      const time = cloneDeep(item);

      time.start = getTimeStart(time);
      time.end = getTimeEnd(time);

      return time;
    });
  };

  const getTimeStart = React.useCallback(
    (time) => {
      return moment(currentDay)
        .set({
          hours: time.start.substr(0, 2),
          minutes: time.start.substr(2),
        })
        .format("YYYY-MM-DDTHH:mmZ");
    },
    [currentDay]
  );

  const getTimeEnd = React.useCallback(
    (time) => {
      return moment(currentDay)
        .set({
          hours: time.end.substr(0, 2),
          minutes: time.end.substr(2),
        })
        .format("YYYY-MM-DDTHH:mmZ");
    },
    [currentDay]
  );

  const getCurrentDayWorkingHour = () => {
    let currDayWorkingHours = getCurrentDayWorkingHours();
    let prevDayWorkingHours = getPrevDayWorkingHours();
    const currDayWorkingHoursFormatted = sortCurrentDayWorkingHour(
      currDayWorkingHours,
      prevDayWorkingHours
    );
    return formatTimeForCurrentDay(currDayWorkingHoursFormatted);
  };

  const getWorkingHoursOfDay = (workingHour) => {
    if (!workingHour.length) {
      return {
        start: moment(currentDay).startOf("day").toDate(),
        end: moment(currentDay).endOf("day").toDate(),
        isWorkingDay: false,
      };
    }

    const workingHourMapped = cloneDeep(workingHour);
    let returnWorkingHour = { start: "", end: "", isWorkingDay: true };

    workingHourMapped.forEach((item) => {
      if (!returnWorkingHour.start) {
        returnWorkingHour.start = item.start;
      }
      if (!returnWorkingHour.end) {
        returnWorkingHour.end = item.end;
      }

      if (moment(returnWorkingHour.start).isAfter(item.start)) {
        returnWorkingHour.start = item.start;
      }

      if (moment(returnWorkingHour.end).isBefore(item.end)) {
        returnWorkingHour.end = item.end;
      }
    });

    return returnWorkingHour;
  };

  return { __blockTimes, handleSetBlockTime, currDay, workingHours };
};

export default useBlockTime;
